import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Modal,
  Spin,
  Divider,
  Table, Button, message, notification, Checkbox,
  Row, Col,
} from 'antd';
import { connect, useDispatch } from 'react-redux';
import { isNotNullish, isNullish } from 'utils/nullish';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n.js';
import { ze } from 'utils/zhEn';
import moment from 'moment';
import { upsertServerAppSettings, upsertWebAppSettings } from 'reducers/app/appActions';
import { isMobile } from 'utils/platform';
import { WebAppSettings } from 'utils/web-app-settings';
import { ServerAppSettings } from 'utils/server-app-settings';
import { CS_STYLES } from 'utils/constants/style-constants';
import DeleteIcon from 'svg/v2/newcs_dashboard_projectinformation_projectdelete_icon.svg';

// 驼峰式字段名（TapdModal中使用）到下划线式字段名（API返回）的映射
// 之前有部分字段的定义在我们的代码里面手动转为了驼峰式（例如iteration_id我们之前定义的时候写死成了iterationId)，没有和Tapd接口返回的数据保持一致，这里做一下映射定义
const CAMEL_TO_SNAKE_CASE_FIELD_MAP = {
  severity: 'severity',
  reporter: 'reporter',
  versionReport: 'version_report',
  iterationId: 'iteration_id',
  module: 'module',
  releaseId: 'release_id',
  priority: 'priority',
  currentOwner: 'current_owner',
  baseline_find: 'baseline_find',
  os: 'os',
  frequency: 'frequency',
};

// 下划线式到驼峰式的反向映射
const SNAKE_TO_CAMEL_CASE_FIELD_MAP = Object.fromEntries(
  Object.entries(CAMEL_TO_SNAKE_CASE_FIELD_MAP).map(([k, v]) => [v, k])
);

const BUILT_IN_FIELD_INFO_LIST = [
  { value: 'severity', label: i18n.t('tapd.严重程度') },
  { value: 'reporter', label: i18n.t('tapd.创建人'), mustRequired: true },
  { value: 'versionReport', label: i18n.t('tapd.发现版本') },
  { value: 'iterationId', label: i18n.t('tapd.迭代') },
  { value: 'module', label: i18n.t('tapd.模块') },
  { value: 'releaseId', label: i18n.t('tapd.发布计划') },
  { value: 'priority', label: i18n.t('tapd.优先级') },
  { value: 'currentOwner', label: i18n.t('tapd.处理人') },
  { value: 'baseline_find', label: '发现基线' },
  { value: 'os', label: '操作系统' },
  { value: 'frequency', label: '重现规律' },
];

// 通用字段默认值列表（下划线式字段名），如果项目没有配置过字段，则使用这个列表
const DEFAULT_BUILT_IN_FIELD_KEYS = [
  'severity',
  'reporter',
  'version_report',
  'iteration_id',
  'module',
  'release_id',
  'priority',
  'current_owner',
  'baseline_find',
  'os',
  'frequency',
];

// 以下字段不得出现在配置界面
const UNSUPPORTED_BUILT_IN_FIELD_KEYS = [
  'id', // TAPD单ID
  'title', // 标题是在单独的区域编辑
  'description', // 描述是在单独的区域编辑
  'created', // 创建时间
  'reopen_time', // 重新打开时间
  'closed', // 关闭时间
  'modified', // 最后修改时间
];
const UNSUPPORTED_BUILT_IN_FIELD_KEY_SET = new Set(UNSUPPORTED_BUILT_IN_FIELD_KEYS);

const KEY_TO_BUILT_IN_FIELD_INFO = Object.assign(Object.create(null), ...BUILT_IN_FIELD_INFO_LIST.map(x => ({ [x.value]: x })));

export const TapdEditBugCustomFieldsUtil = (() => {
  return Object.freeze({
    getBuiltInFieldInfoList() {
      return BUILT_IN_FIELD_INFO_LIST;
    },

    getBuiltInFieldInfoByKey(fieldKey) {
      return KEY_TO_BUILT_IN_FIELD_INFO[fieldKey];
    },

    checkIsCustomField(fieldKey) {
      return (fieldKey || '').startsWith('custom_field_');
    },

    getSettingKey() {
      return WebAppSettings.keys.tapdEnabledBugCustomFields;
    },

    /**
     * @param {{ field: string, required: boolean }[]} tapdFieldList 来自ServerAppSettings的tapdFieldList配置项
     * @param {{ customField: string }[]} tapdEnabledBugCustomFields 来自WebAppSettings的tapdEnabledBugCustomFields配置项，仅用作CS 1.0兼容老数据使用，后续废弃
     * @returns {{ field: string, required: boolean }[]} 返回的field是驼峰式字段名，用于TapdModal
     * */
    getEnabledFieldList(tapdFieldList, tapdEnabledBugCustomFields) {
      if (tapdFieldList && tapdFieldList.length > 0) {
        // 将下划线式字段名转换为驼峰式字段名（因为TapdModal使用驼峰式）
        return tapdFieldList.map(x => ({
          field: SNAKE_TO_CAMEL_CASE_FIELD_MAP[x.field] || x.field,
          required: x.required,
        }));
      }
      const defaultBuiltInFieldList = BUILT_IN_FIELD_INFO_LIST.map(x => ({ field: x.value, required: !!x.mustRequired }));
      const customFieldList = tapdEnabledBugCustomFields.map(x => ({ field: x.customField, required: false }));
      return [
        ...defaultBuiltInFieldList,
        ...customFieldList,
      ];
    },
  });
})();

const TapdEditBugCustomFieldsModal = ({
  reduxState,
  visible,
  bugFieldInfoList,
  onCancel = () => {},
  onSave = () => {},
}) => {
  bugFieldInfoList = bugFieldInfoList || [];

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { appId, platformId } = reduxState.app.get('current').toJS();
  const settings = reduxState.app.get('appIdToWebAppSettings')[appId];
  const selectedFieldsFromSettings = (settings || {})[TapdEditBugCustomFieldsUtil.getSettingKey()] || [];
  const serverAppSettings = reduxState.app.get('appIdToServerAppSettings')[appId] || {};
  const tapdFieldList = serverAppSettings[ServerAppSettings.keys.tapdFieldList];

  const [loading, setLoading] = useState(false);

  const [selectedBuiltInFieldKeys, setSelectedBuiltInFieldKeys] = useState([]);
  const [selectedCustomFieldKeys, setSelectedCustomFieldKeys] = useState([]);
  const [requiredFieldKeyDict, setRequiredFieldKeyDict] = useState({});

  const initializedRef = useRef(false);

  // 从bugFieldInfoList中提取自定义字段（custom_field_开头）
  const customFieldOptions = useMemo(() => bugFieldInfoList
    .filter(x => (x.name || '').startsWith('custom_field_'))
    .map(x => ({
      value: x.name,
      label: x.label,
      type: x.html_type,
      options: x.options,
    })), [bugFieldInfoList]);

  // 从bugFieldInfoList中提取通用字段（非custom_开头，且不是custom_plan_field_等其他字段）
  const builtInFieldOptionsFromApi = useMemo(() => bugFieldInfoList
    .filter(x => {
      const name = x.name || '';
      return !name.startsWith('custom_') && !UNSUPPORTED_BUILT_IN_FIELD_KEY_SET.has(name);
    })
    .map(x => ({
      value: x.name,
      label: x.label,
    })), [bugFieldInfoList]);

  // 通用字段排序：先是默认列表的字段，然后是API返回的其他通用字段
  const builtInFieldOptions = useMemo(() => [
    ...DEFAULT_BUILT_IN_FIELD_KEYS
      .filter(key => builtInFieldOptionsFromApi.some(x => x.value === key))
      .map(key => builtInFieldOptionsFromApi.find(x => x.value === key)),
    ...builtInFieldOptionsFromApi.filter(x => !DEFAULT_BUILT_IN_FIELD_KEYS.includes(x.value)),
  ], [builtInFieldOptionsFromApi]);

  const allFieldOptions = useMemo(() => [...builtInFieldOptions, ...customFieldOptions], [builtInFieldOptions, customFieldOptions]);

  const allSelectedFieldKeys = useMemo(() => [...selectedBuiltInFieldKeys, ...selectedCustomFieldKeys], [selectedBuiltInFieldKeys, selectedCustomFieldKeys]);

  const allOrderedSelectedFieldKeys = useMemo(() => allFieldOptions
    .filter(x => allSelectedFieldKeys.includes(x.value))
    .map(x => x.value), [allFieldOptions, allSelectedFieldKeys]);

  // Modal 打开时初始化字段选择
  useEffect(() => {
    if (!visible) {
      // Modal 关闭时重置初始化标记
      initializedRef.current = false;
      return;
    }

    // 如果已经初始化过，或者数据还没准备好，则跳过
    if (initializedRef.current || builtInFieldOptions.length === 0) {
      return;
    }

    // getEnabledFieldList 返回的是驼峰式字段名
    const enabledFieldList = TapdEditBugCustomFieldsUtil.getEnabledFieldList(tapdFieldList, selectedFieldsFromSettings);
    const enabledBuiltInFieldList = enabledFieldList.filter(x => !TapdEditBugCustomFieldsUtil.checkIsCustomField(x.field));
    const enabledCustomFieldList = enabledFieldList.filter(x => TapdEditBugCustomFieldsUtil.checkIsCustomField(x.field));

    // 从API返回的通用字段中筛选出已启用的字段
    // builtInFieldOptions 中的字段名是下划线式（来自API）
    // enabledBuiltInFieldList 中的字段名是驼峰式
    const builtInFieldKeys = builtInFieldOptions
      .filter(x => {
        const camelCaseFieldKey = SNAKE_TO_CAMEL_CASE_FIELD_MAP[x.value] || x.value;
        const mustRequired = KEY_TO_BUILT_IN_FIELD_INFO[camelCaseFieldKey]?.mustRequired;
        return mustRequired || enabledBuiltInFieldList.some(y => y.field === camelCaseFieldKey);
      })
      .map(x => x.value);
    setSelectedBuiltInFieldKeys(builtInFieldKeys);
    setSelectedCustomFieldKeys(enabledCustomFieldList.map(x => x.field));

    // 构建 requiredFieldKeyDict，需要同时支持驼峰式和下划线式
    const requiredDict = Object.create(null);
    enabledFieldList.filter(x => x.required).forEach(x => {
      // x.field 是驼峰式，需要转换为下划线式
      const snakeCaseFieldKey = CAMEL_TO_SNAKE_CASE_FIELD_MAP[x.field] || x.field;
      requiredDict[snakeCaseFieldKey] = true;
    });
    setRequiredFieldKeyDict(requiredDict);

    // 标记已初始化
    initializedRef.current = true;
  }, [visible, builtInFieldOptions, tapdFieldList, selectedFieldsFromSettings]);

  const handleBuiltInFieldsChange = useCallback((v) => {
    setSelectedBuiltInFieldKeys(v);
  }, []);

  const handleCustomFieldsChange = useCallback((v) => {
    setSelectedCustomFieldKeys(v);
  }, []);

  const onSubmit = useCallback(() => {
    setLoading(true);
    // allOrderedSelectedFieldKeys 中的字段名是下划线式（来自API），保存时也使用下划线式
    const newTapdFieldList = allOrderedSelectedFieldKeys.map(x => {
      // 如果是旧的驼峰式字段名，需要转换为下划线式
      const camelCaseFieldKey = SNAKE_TO_CAMEL_CASE_FIELD_MAP[x] || x;
      const nullableBuiltInFieldInfo = TapdEditBugCustomFieldsUtil.getBuiltInFieldInfoByKey(camelCaseFieldKey);
      const mustRequired = nullableBuiltInFieldInfo?.mustRequired;
      return {
        field: x, // 保存下划线式字段名
        required: mustRequired || !!requiredFieldKeyDict[x],
      };
    });

    dispatch(upsertServerAppSettings(appId, {
      [ServerAppSettings.keys.tapdFieldList]: newTapdFieldList,
    }));

    setLoading(false);
    onSave();
  }, [allOrderedSelectedFieldKeys, requiredFieldKeyDict, appId, dispatch, onSave]);

  return <Modal
    title={ze('设置TAPD字段', 'Set TAPD Fields')}
    width='1200px'
    bodyStyle={{ minHeight: '200px' }}
    visible={visible}
    maskClosable={false}
    onCancel={() => onCancel()}
    okText={ze('保存', 'Save')}
    okButtonProps={{ disabled: loading }}
    onOk={() => onSubmit()}
  >
    <Spin spinning={loading}><div style={{ display: 'flex' }}>
      <div
        style={{ flexGrow: 1, maxHeight: 'max(calc(100vh - 320px), 300px)', overflowY: 'auto' }}
      >
        <div style={{marginBottom: '20px'}}>{ ze('项目级设置。勾选的字段将可以在提交缺陷界面中填写。', 'This is app setting. Choose the fields you want to use.') }</div>
        <div style={{ marginBottom: '12px', fontWeight: 700 }}>{ ze('通用字段', 'Built-In Fields') }</div>
        <div>
          <Checkbox.Group
            style={{ width: '100%' }}
            value={selectedBuiltInFieldKeys}
            onChange={handleBuiltInFieldsChange}
          ><Row> {
            builtInFieldOptions.map(item => {
              // item.value 是下划线式字段名，需要转换为驼峰式来查询 mustRequired
              const camelCaseFieldKey = SNAKE_TO_CAMEL_CASE_FIELD_MAP[item.value] || item.value;
              const mustRequired = KEY_TO_BUILT_IN_FIELD_INFO[camelCaseFieldKey]?.mustRequired;
              return <Col span={6} key={item.value} style={{marginBottom: '16px'}}>
                <Checkbox
                  disabled={mustRequired}
                  value={item.value}
                >{item.label}</Checkbox>
              </Col>;
            })
          } </Row></Checkbox.Group>
        </div>
        <div style={{ marginTop: '12px', marginBottom: '12px', fontWeight: 700 }}>{ ze('自定义字段', 'Custom Fields') }</div>
        <div>
          <Checkbox.Group
            style={{ width: '100%' }}
            value={selectedCustomFieldKeys}
            onChange={handleCustomFieldsChange}
          >
            <Row>
              {
                customFieldOptions.map(item => <Col span={6} key={item.value} style={{marginBottom: '16px'}}>
                  <Checkbox value={item.value}>{item.label}</Checkbox>
                </Col>)
              }
            </Row>
          </Checkbox.Group>
        </div>
      </div>
      <Divider type='vertical' style={{ height: 'auto' }}/>
      <div style={{ width: '200px', flexShrink: 0 }}>
        <div style={{ color: CS_STYLES.SUB_TEXT_COLOR }}>{ ze('当前选定的字段', 'Selected Fields') }</div>
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>{
          allOrderedSelectedFieldKeys.map(x => {
            const isCustomField = TapdEditBugCustomFieldsUtil.checkIsCustomField(x);
            // x 是下划线式字段名，需要转换为驼峰式来查询 mustRequired
            const camelCaseFieldKey = SNAKE_TO_CAMEL_CASE_FIELD_MAP[x] || x;
            const nullableBuiltInFieldInfo = TapdEditBugCustomFieldsUtil.getBuiltInFieldInfoByKey(camelCaseFieldKey);
            const mustRequired = !!nullableBuiltInFieldInfo?.mustRequired;
            const required = mustRequired || !!requiredFieldKeyDict[x];

            const label = isCustomField
              ? customFieldOptions.find(y => y.value === x)?.label || x
              : builtInFieldOptions.find(y => y.value === x)?.label || x;
            return <div key={x} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ flexGrow: 1 }}>{ label }</div>
              <Checkbox
                checked={required}
                disabled={mustRequired}
                onChange={(e) => setRequiredFieldKeyDict({ ...requiredFieldKeyDict, [x]: e.target.checked })}
              >{ ze('必填', 'Required') }</Checkbox>
              {!mustRequired && (
                <DeleteIcon
                  style={{ color: CS_STYLES.SUB_TEXT_COLOR, cursor: 'pointer' }}
                  onClick={() => {
                    if (isCustomField) {
                      setSelectedCustomFieldKeys(selectedCustomFieldKeys.filter(y => y !== x));
                    } else {
                      setSelectedBuiltInFieldKeys(selectedBuiltInFieldKeys.filter(y => y !== x));
                    }
                  }}
                />
              )}
              {mustRequired && <div style={{ width: '16px', height: '16px' }}></div>}
            </div>;
          })
        }</div>
      </div>
    </div></Spin>
  </Modal>;
};

TapdEditBugCustomFieldsModal.propTypes = {
};

const mapStateToProps = state => ({
  reduxState: state,
});

export default connect(mapStateToProps)(TapdEditBugCustomFieldsModal);
